import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import type { RefObject } from 'react'

import {
  isGuideTourQueueLabelHidden,
  isGuideTourViewedOnStepEnabled,
} from '../config'
import { useGuideTour } from '../provider/guideTourContext'
import { hasGuideTourStepTarget } from '../utils/targets'
import { buildGuideTourSkippedStepNoticeMessage } from '../utils/tourRuntime'
import type { GuideTourStep, GuideTourStepCardSize } from '../types'
import type { SpotlightBounds } from '../overlay/utils'
import {
  getGuideTourStepCardEstimatedHeight,
  getGuideTourStepCardWidth,
  resolveGuideTourStepCardSize,
  getScreenStepCardPosition,
  getStepCardLayout,
  type GuideTourStepCardArrowSide,
} from './utils'

const resolveGuideTourStepCardContinueLabel = (
  isTourUnavailablePage: boolean,
  isSummaryOnly: boolean,
  shouldCompleteOnContinue: boolean
): string => {
  if (isSummaryOnly && !isTourUnavailablePage) {
    return 'Concluir'
  }
  if (isTourUnavailablePage || shouldCompleteOnContinue) {
    return 'Entendi'
  }
  return 'Continuar'
}

const shouldHideGuideTourStepCard = (input: {
  isActive: boolean
  currentStep: GuideTourStep | null
  isSummaryOnly: boolean
  isCurrentStepReady: boolean
  hasTarget: boolean
  targetBounds: SpotlightBounds | null
}): boolean => {
  if (!input.isActive || !input.currentStep) {
    return true
  }
  if (!input.isSummaryOnly && !input.isCurrentStepReady) {
    return true
  }
  if (!input.isSummaryOnly && input.hasTarget && !input.targetBounds) {
    return true
  }
  return false
}

export type GuideTourStepCardModel = {
  cardRef: RefObject<HTMLDivElement>
  top: number
  left: number
  arrowSide: GuideTourStepCardArrowSide | null
  isInModalLayer: boolean
  isSummaryOnly: boolean
  isTourUnavailablePage: boolean
  currentStep: GuideTourStep
  currentStepNumber: number
  stepCount: number
  isFirstStep: boolean
  continueLabel: string
  routeTourQueue: { position: number; total: number } | null
  skippedStepNoticeMessage: string | null
  isBlockedTargetInteractionWarningVisible: boolean
  blockedTargetInteractionFeedbackKey: number | undefined
  cardSize: GuideTourStepCardSize
  handleSkip: () => void
  handleNext: () => void
  handlePrev: () => void
}

const useGuideTourStepCardModel = (): GuideTourStepCardModel | null => {
  const {
    state,
    currentStep,
    currentStepNumber,
    stepCount,
    isFirstStep,
    shouldCompleteOnContinue,
    next,
    prev,
    skip,
    markStepViewed,
    notifyStepInteraction,
    targetBounds,
    isCurrentStepReady,
    isInModalLayer,
    getRouteTourQueue,
    currentPathname,
    blockedTargetInteractionFeedbackKey,
    isBlockedTargetInteractionWarningVisible,
    clearBlockedTargetInteractionWarning,
    skippedStepNotice,
    isSkippedStepNoticeVisible,
    isSkippedStepsSummaryActive,
    clearSkippedStepNotice,
    completeSkippedStepsSummary,
    dismissTourUnavailableOnPage,
  } = useGuideTour()
  const cardRef = useRef<HTMLDivElement>(null)
  const openingStartEmittedRef = useRef(false)
  const lastViewedStepIndexRef = useRef(-1)
  const [cardHeight, setCardHeight] = useState(0)
  const isActive = state.status === 'active'
  const hasTarget = hasGuideTourStepTarget(currentStep)
  const cardSize = resolveGuideTourStepCardSize(
    currentStep,
    state.activeTour ?? undefined
  )

  useLayoutEffect(() => {
    const card = cardRef.current

    if (!card) {
      return undefined
    }

    const updateCardHeight = () => {
      setCardHeight(card.getBoundingClientRect().height)
    }

    updateCardHeight()

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateCardHeight)
        : null

    resizeObserver?.observe(card)

    return () => {
      resizeObserver?.disconnect()
    }
  }, [
    currentStep,
    isActive,
    targetBounds,
    hasTarget,
    isBlockedTargetInteractionWarningVisible,
    isSkippedStepNoticeVisible,
    isSkippedStepsSummaryActive,
    cardSize,
  ])

  useEffect(() => {
    openingStartEmittedRef.current = false
    lastViewedStepIndexRef.current = -1
  }, [state.activeTourId])

  useEffect(() => {
    if (!isActive || !currentStep) {
      return
    }

    const isSummaryOnly = isSkippedStepsSummaryActive

    if (isSummaryOnly && skippedStepNotice?.tourUnavailableOnPage) {
      return
    }

    if (!isSummaryOnly && !isCurrentStepReady) {
      return
    }

    if (hasTarget && !targetBounds) {
      return
    }

    const stepPayload = {
      currentStep: currentStepNumber,
      totalStep: stepCount,
    }

    if (!isSummaryOnly && !openingStartEmittedRef.current) {
      openingStartEmittedRef.current = true
      lastViewedStepIndexRef.current = state.currentStepIndex
      notifyStepInteraction('start', stepPayload)
      markStepViewed()
      return
    }

    if (lastViewedStepIndexRef.current === state.currentStepIndex) {
      return
    }

    lastViewedStepIndexRef.current = state.currentStepIndex

    if (!isSummaryOnly && isGuideTourViewedOnStepEnabled(state.activeTour)) {
      notifyStepInteraction('viewed', stepPayload)
    }

    markStepViewed()
  }, [
    currentStep,
    currentStepNumber,
    hasTarget,
    isActive,
    isCurrentStepReady,
    isSkippedStepsSummaryActive,
    markStepViewed,
    notifyStepInteraction,
    skippedStepNotice?.tourUnavailableOnPage,
    state.activeTour,
    state.currentStepIndex,
    stepCount,
    targetBounds,
  ])

  if (
    shouldHideGuideTourStepCard({
      isActive,
      currentStep,
      isSummaryOnly: isSkippedStepsSummaryActive,
      isCurrentStepReady,
      hasTarget,
      targetBounds,
    })
  ) {
    return null
  }

  if (!currentStep) {
    return null
  }

  const isSummaryOnly = isSkippedStepsSummaryActive
  const isTourUnavailablePage = Boolean(
    isSummaryOnly && skippedStepNotice?.tourUnavailableOnPage
  )

  const skippedStepNoticeMessage =
    isSkippedStepNoticeVisible && skippedStepNotice
      ? buildGuideTourSkippedStepNoticeMessage(skippedStepNotice)
      : null

  const cardWidth = Math.min(
    getGuideTourStepCardWidth(cardSize),
    window.innerWidth - 32
  )
  const layoutInput = {
    cardWidth,
    cardHeight: cardHeight || getGuideTourStepCardEstimatedHeight(cardSize),
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  }

  let top: number
  let left: number
  let arrowSide: GuideTourStepCardArrowSide | null

  if (isSummaryOnly || !hasTarget || !targetBounds) {
    const centered = getScreenStepCardPosition(layoutInput)
    top = centered.top
    left = centered.left
    arrowSide = null
  } else {
    const layout = getStepCardLayout({
      bounds: targetBounds,
      position: currentStep.position ?? 'auto',
      ...layoutInput,
    })
    top = layout.top
    left = layout.left
    arrowSide = layout.arrowSide
  }

  const continueLabel = resolveGuideTourStepCardContinueLabel(
    isTourUnavailablePage,
    isSummaryOnly,
    shouldCompleteOnContinue
  )
  const routeTourQueue =
    state.activeTourId !== null && !isGuideTourQueueLabelHidden()
      ? getRouteTourQueue(currentPathname, state.activeTourId)
      : null

  const handleSkip = () => {
    clearBlockedTargetInteractionWarning()
    clearSkippedStepNotice()
    notifyStepInteraction('dismiss', {
      currentStep: currentStepNumber,
      totalStep: stepCount,
    })
    skip()
  }

  const handleNext = () => {
    clearBlockedTargetInteractionWarning()

    if (isTourUnavailablePage) {
      dismissTourUnavailableOnPage()
      return
    }

    clearSkippedStepNotice()

    if (isSummaryOnly) {
      completeSkippedStepsSummary({
        currentStep: currentStepNumber,
        totalStep: stepCount,
      })
      return
    }

    notifyStepInteraction(shouldCompleteOnContinue ? 'complete' : 'next', {
      currentStep: currentStepNumber,
      totalStep: stepCount,
    })
    next()
  }

  const handlePrev = () => {
    clearBlockedTargetInteractionWarning()
    notifyStepInteraction('previous', {
      currentStep: currentStepNumber,
      totalStep: stepCount,
    })
    prev()
  }

  return {
    cardRef,
    top,
    left,
    arrowSide,
    isInModalLayer,
    isSummaryOnly,
    isTourUnavailablePage,
    currentStep,
    currentStepNumber,
    stepCount,
    isFirstStep,
    continueLabel,
    routeTourQueue,
    skippedStepNoticeMessage,
    isBlockedTargetInteractionWarningVisible,
    blockedTargetInteractionFeedbackKey,
    cardSize,
    handleSkip,
    handleNext,
    handlePrev,
  }
}

export { useGuideTourStepCardModel }
